import { FC } from 'react';
import { iconType } from '../assets/icons/icon-types';
import Manage from '../assets/icons/manage-icon';
import Config from '../assets/icons/config-icon';
import TransactionsIcon from '../assets/icons/transactions-icon';
import SearchIcon from '../assets/icons/search-icon';
import Configure from '../pages/configure';
import DeviceDetail from '../pages/device-detail';
import Transactions from '../pages/transactions';
import VIMSTransactions from '../pages/vims-transactions';
import Reports from '../pages/reports';
import Admin from '../pages/admin';
import { AppPermissions, KeyTypes } from '../entities/entitlements-v1/authorizations-parameters';
import BulkDeviceManagement from '../pages/bulk-device-management';
import BulkDeviceIcon from '../assets/icons/bulk-device-icon';
import SoftwareLibraryManagement from '../pages/software-library-management';
import LibraryListIcon from '../assets/icons/library-list-icon';
import ReportsIcon from '../assets/icons/reports-icon';
import { EnvironmentNames, deployUpTo } from '../utilities';
import AdminIcon from '../assets/icons/admin-icon';
import FileIcon from '../assets/icons/file-icon';

const deployFeatureUpToDevelopment = deployUpTo(EnvironmentNames.integration);

export type NavSection = { [key: string]: NavObject };

export type NavObject = {
  key: string;
  title: string;
  uri: string;
  icon: FC<iconType>;
  path: string;
  component: any;
  authAccessor: string;
  carryDevice: boolean;
};

export const navList: NavObject[] = [
  {
    key: 'transactions',
    title: 'Transactions',
    uri: '/transactions',
    icon: TransactionsIcon,
    path: '/transactions/:deviceId?',
    component: Transactions,
    authAccessor: 'transaction',
    carryDevice: true,
  },
  {
    key: 'vims-transactions',
    title: 'VIMS Transactions',
    uri: '/vims-transactions',
    icon: FileIcon,
    path: '/vims-transactions/:telematicsAssetId?',
    component: VIMSTransactions,
    authAccessor: 'transaction',
    carryDevice: true,
  },
  {
    key: 'configure',
    title: 'Configuration',
    uri: '/configure',
    icon: Config,
    path: '/configure/:deviceId?',
    component: Configure,
    authAccessor: 'configuration',
    carryDevice: true,
  },
  {
    key: 'bulk-device-management',
    title: 'Bulk Device',
    uri: '/bulk-device-management',
    icon: BulkDeviceIcon,
    path: '/bulk-device-management',
    component: BulkDeviceManagement,
    authAccessor: 'bulkDeviceManagement',
    carryDevice: false,
  },
  {
    key: 'device-details',
    title: 'Device Details',
    uri: '/device-details',
    icon: Manage,
    path: '/device-details/:deviceId?',
    component: DeviceDetail,
    authAccessor: 'deviceDetails',
    carryDevice: true,
  },
  {
    key: 'software-library-management',
    title: 'Software Library',
    uri: '/software-library-management',
    icon: LibraryListIcon,
    path: '/software-library-management',
    component: SoftwareLibraryManagement,
    authAccessor: 'firmwareLibrary',
    carryDevice: false,
  },
  {
    key: 'reports',
    title: 'Reports',
    uri: '/reports',
    icon: ReportsIcon,
    path: '/reports',
    component: Reports,
    authAccessor: 'reports',
    carryDevice: true,
  },
  {
    key: 'admin',
    title: 'Admin',
    uri: '/admin',
    icon: AdminIcon,
    path: '/admin',
    component: Admin,
    authAccessor: 'Administration',
    carryDevice: false,
  },
];

const pagesUnderConstruction: NavObject[] = [];

export const navListAuthBased = (appPermissions: AppPermissions | undefined): NavSection | null => {
  if (appPermissions) {
    const authorizedNavList: NavSection = {};
    navList.forEach((navItem) => {
      if (navItem.key === 'admin') {
        const adminModules = appPermissions.iot?.Administration ?? [];
        const hasAdminModules =
          Array.isArray(adminModules) &&
          adminModules.some((key) =>
            [
              'userManagement',
              'roleManagement',
              'messageReplay',
              'deviceOnboarding',
              'fileTypeConfiguration',
              'bulkFileTypeConfiguration',
            ].includes(key),
          );
        if (hasAdminModules) {
          authorizedNavList[navItem.key] = navItem;
        }
        return;
      }
      if (navItem.authAccessor && !!appPermissions.iot[navItem.authAccessor as KeyTypes])
        authorizedNavList[navItem.key] = navItem;
    });
    if (deployFeatureUpToDevelopment && pagesUnderConstruction.length > 0)
      pagesUnderConstruction.forEach((staticPage) => {
        authorizedNavList[staticPage.key] = staticPage;
      });

    return authorizedNavList;
  }
  return null;
};
